import { z } from "zod"

const catalogCodeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)
const lookupKeySchema = z
  .string()
  .regex(/^impetusos_[a-z0-9_]{3,96}_v[1-9][0-9]*$/)

export const platformBillingPriceV1Schema = z.object({
  billingPeriod: z.enum(["month", "year"]),
  amountMinor: z.number().int().positive(),
  currency: z.literal("usd"),
  providerLookupKey: lookupKeySchema,
  taxBehavior: z.literal("exclusive"),
}).strict()

export const platformBillingCatalogItemV1Schema = z.object({
  kind: z.enum(["plan", "add_on"]),
  code: catalogCodeSchema,
  revision: z.number().int().positive(),
  providerProductMetadataValue: catalogCodeSchema,
  prices: z.array(platformBillingPriceV1Schema).length(2),
}).strict().superRefine((item, context) => {
  const periods = new Set(item.prices.map((price) => price.billingPeriod))
  if (periods.size !== item.prices.length) {
    context.addIssue({
      code: "custom",
      path: ["prices"],
      message: "A catalog item cannot define the same billing period twice.",
    })
  }
})

export const connectTransactionFeePolicyV1Schema = z.object({
  policyVersion: z.number().int().positive(),
  scope: z.literal("stripe_connect_direct_charges"),
  basisPoints: z.number().int().min(0).max(1_000),
  rounding: z.literal("nearest_cent_half_up"),
  minimumFeeMinor: z.number().int().nonnegative(),
  maximumFeeMinor: z.number().int().positive().nullable(),
  fullRefundBehavior: z.literal("refund_application_fee"),
  partialRefundBehavior: z.literal("proportional"),
  effectiveFrom: z.iso.datetime({ offset: true }),
  effectiveUntil: z.iso.datetime({ offset: true }).nullable(),
}).strict().superRefine((policy, context) => {
  if (
    policy.effectiveUntil !== null
    && Date.parse(policy.effectiveUntil) <= Date.parse(policy.effectiveFrom)
  ) {
    context.addIssue({
      code: "custom",
      path: ["effectiveUntil"],
      message: "A transaction-fee policy must end after it starts.",
    })
  }
  if (
    policy.maximumFeeMinor !== null
    && policy.maximumFeeMinor < policy.minimumFeeMinor
  ) {
    context.addIssue({
      code: "custom",
      path: ["maximumFeeMinor"],
      message: "The maximum fee cannot be lower than the minimum fee.",
    })
  }
})

export const platformBillingTermsV1Schema = z.object({
  termsVersion: z.string().regex(/^impetusos-saas-v[1-9][0-9]*$/),
  trialDays: z.number().int().min(0).max(90),
  collectionMethod: z.literal("charge_automatically"),
  automaticTax: z.literal("disabled_until_registration"),
  taxBehavior: z.literal("exclusive"),
  dunningStrategy: z.literal("stripe_smart_retries"),
  graceDays: z.number().int().min(1).max(60),
  terminalEntitlementAction: z.literal("restrict"),
  preservesExportSupportAndHistory: z.literal(true),
}).strict()

export const platformBillingCatalogV1Schema = z.object({
  contractVersion: z.literal(1),
  catalogVersion: z.number().int().positive(),
  effectiveFrom: z.iso.datetime({ offset: true }),
  effectiveUntil: z.iso.datetime({ offset: true }).nullable(),
  items: z.array(platformBillingCatalogItemV1Schema).min(1).max(32),
  terms: platformBillingTermsV1Schema,
  connectTransactionFee: connectTransactionFeePolicyV1Schema,
}).strict().superRefine((catalog, context) => {
  if (
    catalog.effectiveUntil !== null
    && Date.parse(catalog.effectiveUntil) <= Date.parse(catalog.effectiveFrom)
  ) {
    context.addIssue({
      code: "custom",
      path: ["effectiveUntil"],
      message: "A billing catalog must end after it starts.",
    })
  }
  const items = new Set<string>()
  const lookupKeys = new Set<string>()
  for (const [itemIndex, item] of catalog.items.entries()) {
    const itemKey = `${item.kind}:${item.code}:${item.revision}`
    if (items.has(itemKey)) {
      context.addIssue({
        code: "custom",
        path: ["items", itemIndex],
        message: "A catalog revision cannot be defined twice.",
      })
    }
    items.add(itemKey)
    for (const [priceIndex, price] of item.prices.entries()) {
      if (lookupKeys.has(price.providerLookupKey)) {
        context.addIssue({
          code: "custom",
          path: ["items", itemIndex, "prices", priceIndex, "providerLookupKey"],
          message: "A provider lookup key must be unique.",
        })
      }
      lookupKeys.add(price.providerLookupKey)
    }
  }
})

export const connectTransactionFeeQuoteV1Schema = z.object({
  responseVersion: z.literal(1),
  policyVersion: z.number().int().positive(),
  chargeAmountMinor: z.number().int().positive(),
  applicationFeeAmountMinor: z.number().int().nonnegative(),
  currency: z.literal("usd"),
  collectable: z.boolean(),
  reason: z.enum(["fee_applied", "fee_disabled", "charge_too_small"]),
}).strict()

export const platformBillingWebhookEventTypesV1 = [
  "customer.created",
  "customer.updated",
  "customer.deleted",
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "customer.subscription.paused",
  "customer.subscription.resumed",
  "customer.subscription.trial_will_end",
  "invoice.created",
  "invoice.finalized",
  "invoice.paid",
  "invoice.payment_action_required",
  "invoice.payment_failed",
  "invoice.updated",
  "invoice.voided",
  "invoice.marked_uncollectible",
  "invoice.upcoming",
  "payment_intent.created",
  "payment_intent.succeeded",
  "payment_intent.payment_failed",
  "payment_intent.canceled",
  "credit_note.created",
  "credit_note.updated",
  "credit_note.voided",
  "refund.created",
  "refund.updated",
  "refund.failed",
  "charge.refunded",
] as const

export const platformBillingWebhookEventTypeV1Schema = z.enum(
  platformBillingWebhookEventTypesV1,
)

export const verifiedPlatformBillingEventV1Schema = z.object({
  contractVersion: z.literal(1),
  provider: z.literal("stripe_billing"),
  providerEventId: z.string().regex(/^evt_[A-Za-z0-9]{8,64}$/),
  eventType: platformBillingWebhookEventTypeV1Schema,
  providerObjectId: z.string().regex(/^[a-z]+_[A-Za-z0-9]{8,128}$/),
  createdAtEpoch: z.number().int().nonnegative(),
  livemode: z.literal(false),
  apiVersion: z.string().min(1).max(64).nullable(),
  payloadHash: z.string().regex(/^[a-f0-9]{64}$/),
  platformNamespaceOnly: z.literal(true),
}).strict()

export const platformBillingProviderEvidenceV1Schema = z.object({
  evidenceVersion: z.literal(1),
  provider: z.literal("stripe_billing"),
  environment: z.literal("sandbox"),
  providerAccountRef: z.string().regex(/^acct_[A-Za-z0-9]{8,64}$/),
  accountDetailsSubmitted: z.boolean(),
  catalogMatches: z.boolean(),
  restrictedKeyVerified: z.boolean(),
  keyRotationVerified: z.boolean(),
  webhookRotationVerified: z.boolean(),
  webhookEventsVerified: z.array(platformBillingWebhookEventTypeV1Schema),
  apiEventRetentionDays: z.literal(30),
  dashboardEventSummaryMonths: z.literal(13),
  internalFinancialRetentionYears: z.literal(7),
  productionAccountApproved: z.boolean(),
  separateFromStripeConnect: z.literal(true),
  containsSecretValues: z.literal(false),
}).strict()

export type PlatformBillingCatalogV1 = z.infer<
  typeof platformBillingCatalogV1Schema
>
export type ConnectTransactionFeePolicyV1 = z.infer<
  typeof connectTransactionFeePolicyV1Schema
>
export type ConnectTransactionFeeQuoteV1 = z.infer<
  typeof connectTransactionFeeQuoteV1Schema
>
export type VerifiedPlatformBillingEventV1 = z.infer<
  typeof verifiedPlatformBillingEventV1Schema
>
export type PlatformBillingProviderEvidenceV1 = z.infer<
  typeof platformBillingProviderEvidenceV1Schema
>
export type PlatformBillingWebhookEventTypeV1 = z.infer<
  typeof platformBillingWebhookEventTypeV1Schema
>
